import React, { useEffect, useState } from "react";
import { View } from "react-native";

import Text from "$src/core/components/atoms/Text/Text";
import Toggle from "$src/core/components/atoms/Toggle";
import useSearchClassificationConfiguration from "$src/core/hooks/use-search-classification-configuration";
import useSearchHistory from "$src/core/hooks/use-search-history";
import useStorageState from "$src/core/hooks/use-storage-state";
import useTranslation from "$src/core/hooks/use-translation";
import SearchClassification from "$src/core/services/search-classification";
import ExecutorchEmbedding from "$src/core/services/search-classification/executorch-embedding";
import ExecutorchLlm from "$src/core/services/search-classification/executorch-llm";

import $ from "./SearchSettingsPanel.styles";

const SearchSettingsPanel: React.FC = () => {
  const { t } = useTranslation();
  const { clearHistory } = useSearchHistory();
  const {
    status: conversationalSearchStatus,
    enable,
    disable,
  } = useSearchClassificationConfiguration();

  const [historyPreference, setHistoryPreference] = useStorageState(
    "search.recent.status",
    null,
  );

  const [downloadProgress, setDownloadProgress] = useState<number | null>(null);

  const executorchMode =
    SearchClassification.mode === "executorch-embedding" ||
    SearchClassification.mode === "executorch-llm"
      ? SearchClassification.mode
      : null;

  const isExecutorchEnabled =
    conversationalSearchStatus === "enabled" && executorchMode !== null;

  /* Mirror ExecuTorch download progress */
  useEffect(() => {
    // isExecutorchEnabled already implies executorchMode is set, and TS narrows through it.
    if (!isExecutorchEnabled) return undefined;
    const service =
      executorchMode === "executorch-embedding"
        ? ExecutorchEmbedding
        : ExecutorchLlm;
    const unsubscribe =
      service.addDownloadProgressListener(setDownloadProgress);
    return unsubscribe;
  }, [executorchMode, isExecutorchEnabled]);

  const handleHistoryToggle = (next: boolean) => {
    if (!next) {
      setHistoryPreference("disabled");
      clearHistory();
      return;
    }
    setHistoryPreference("enabled");
  };

  const handleConversationalSearchToggle = async (next: boolean) => {
    if (!next) {
      disable();
      return;
    }
    await enable();
  };

  return (
    <View style={$.panel}>
      <View style={$.setting}>
        <Toggle
          accessibilityRole="checkbox"
          label={t("SEARCH_SETTINGS_PANEL/HISTORY_LABEL")}
          description={t("SEARCH_SETTINGS_PANEL/HISTORY_HINT")}
          value={historyPreference !== "disabled"}
          onChange={handleHistoryToggle}
        />
      </View>

      <View style={$.setting}>
        <Toggle
          accessibilityRole="checkbox"
          label={t("SEARCH_SETTINGS_PANEL/CONVERSATIONAL_SEARCH/LABEL")}
          description={t("SEARCH_SETTINGS_PANEL/CONVERSATIONAL_SEARCH/HINT")}
          value={conversationalSearchStatus === "enabled"}
          onChange={handleConversationalSearchToggle}
        />

        {isExecutorchEnabled &&
          downloadProgress !== null &&
          downloadProgress < 100 && (
            <View
              style={$.download}
              accessibilityRole="progressbar"
              accessibilityValue={{ min: 0, max: 100, now: downloadProgress }}
            >
              <Text preset="tiny" accessibilityRole="text" style={$.hint}>
                {t("SEARCH/CONVERSATIONAL/DOWNLOAD_PROGRESS", {
                  percent: downloadProgress,
                })}
              </Text>
              <View style={$.progressTrack}>
                <View
                  style={$.progressFill({
                    progress: Math.min(downloadProgress, 100),
                  })}
                />
              </View>
            </View>
          )}
      </View>
    </View>
  );
};

export default SearchSettingsPanel;
